import { useEffect, useState, useCallback } from "react";

// MARK: Window Size ------------------------------------------

type WindowSize = {
  width: number | undefined;
  height: number | undefined;
};

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return windowSize;
};

// MARK: Scroll To Section ------------------------------------

export const useScrollToSection = (
  sectionRefs: Record<string, React.RefObject<HTMLDivElement | null>>,
  setActiveSection: (id: string) => void,
  setManuallySelectedSection: (id: string | null) => void,
) => {
  const [isScrolling, setIsScrolling] = useState(false);

  const handleTabClick = useCallback(
    (id: string) => {
      const section = sectionRefs[id]?.current;
      if (section) {
        setIsScrolling(true);
        section.scrollIntoView({ behavior: "smooth" });
        setActiveSection(id);
        setManuallySelectedSection(id);
        setTimeout(() => {
          setIsScrolling(false);
        }, 1000);
      }
    },
    [sectionRefs, setActiveSection, setManuallySelectedSection],
  );

  return { isScrolling, handleTabClick };
};

// MARK: Intersection Observer --------------------------------
export const useIntersectionObserver = (
  sectionRefs: Record<string, React.RefObject<HTMLDivElement | null>>,
  activeSection: string,
  isScrolling: boolean,
  manuallySelectedSection: string | null,
  setActiveSection: (id: string) => void,
  setManuallySelectedSection: (id: string | null) => void,
) => {
  useEffect(() => {
    if (isScrolling) return;

    let currentlyIntersecting = new Map<string, IntersectionObserverEntry>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            currentlyIntersecting.set(entry.target.id, entry);
          } else {
            currentlyIntersecting.delete(entry.target.id);
            if (entry.target.id === manuallySelectedSection) {
              setManuallySelectedSection(null);
            }
          }
        });

        if (currentlyIntersecting.size > 0) {
          let newActiveSection: string = activeSection;

          if (manuallySelectedSection && currentlyIntersecting.has(manuallySelectedSection)) {
            newActiveSection = manuallySelectedSection;
          } else {
            let maxRatio = 0;

            currentlyIntersecting.forEach((entry, id) => {
              const ratio = entry.intersectionRatio;
              const currentTop = entry.boundingClientRect.top;
              const activeTop =
                currentlyIntersecting.get(newActiveSection)?.boundingClientRect.top ?? 0;

              if (ratio > maxRatio || (ratio === maxRatio && currentTop < activeTop)) {
                maxRatio = ratio;
                newActiveSection = id;
              }
            });
          }

          if (newActiveSection !== activeSection) {
            setActiveSection(newActiveSection);
          }
        }
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
        rootMargin: "-1px 0px -1px 0px",
      },
    );

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      Object.values(sectionRefs).forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [
    activeSection,
    sectionRefs,
    isScrolling,
    manuallySelectedSection,
    setActiveSection,
    setManuallySelectedSection,
  ]);
};
